/* eslint-disable import/max-dependencies */
import {
  combineReducers,
  createStore,
  compose,
  applyMiddleware,
  type AnyAction,
  type Store,
} from "redux";

import {
  formReducer,
  initFormState,
  type FormState,
  notificationsReducer,
  initNotificationsState,
  type NotificationsState,
  errorsReducer,
  initErrorsState,
  type ErrorsState,
  loadingReducer,
  initLoadingState,
  type LoadingState,
} from "@fas/ui-framework";

import {
  USER_INFO,
  MANAGER_INFO,
  DASHBOARD_TABLE,
  EVENTS_POSTBACK,
  ADDITIONAL_POSTBACK,
  TRACKING_CODE_LOADING,
  BEST_OFFERS_LIST,
} from "@fas/cpa-state-manager/redux/constants";
import Immutable from "immutable";
import createSagaMiddleware from "redux-saga";
import type {
  DictionariesState,
  EventsPostbackState,
  TrackingToolsState,
  AdditionalPostbackState,
  TableState,
  OutstandingBalanceInfoState,
  LinkGeneratorState,
  ManagerInfoState,
  UserInfoState,
  TabsState,
} from "@fas/cpa-state-manager/redux/reducers";
import {
  managerInfoReducer as managerInfo,
  initManagerInfoState,
  userInfoReducer as userInfo,
  initUserInfoState,
  dictionariesReducer as dictionaries,
  initDictionariesState,
  eventsPostbackReducer as eventsPostback,
  initEventsPostbackState,
  trackingToolsReducer as trackingTools,
  initTrackingToolsState,
  additionalPostbackReducer as additionalPostback,
  initAdditionalPostbackState,
  tableReducer as tables,
  initTableState,
  outstandingBalanceInfoReducer as outstandingBalanceInfo,
  initOutstandingBalanceInfoState,
  linkGeneratorReducer as linkGenerator,
  initLinkGeneratorState,
  tabsReducer as tabs,
  initTabsState,
} from "@fas/cpa-state-manager/redux/reducers";
import mainSaga from "./saga";
import {
  PROFILE_PAGE,
  USER_INFO_TAB,
  CONTACT_INFO_TAB,
  PAYMENT_METHODS_TAB,
  PAYMENT_METHOD_FORM,
  USER_INFO_FORM,
  CONTACT_INFO_FORM,
  REPORTS_PAGE,
  MAIN_REPORT_TAB,
  TRANSACTION_REPORT_TAB,
  MAIN_REPORT_TABLE,
  POSTBACKS_PAGE,
  DEFAULT_POSTBACK_TAB,
  ADDITIONAL_POSTBACKS_TAB,
  INFO_POSTBACKS_TAB,
  OUTSTANDING_BALANCE,
  PAYMENT_HISTORY_TABLE,
  TRANSACTION_REPORT_TABLE,
  optionsList,
  NO_PAYMENT_DETAILS_MODAL,
  NATURE_OF_TRAFFIC_MODAL,
  INFO_BANNER,
  INFO_MAIN_POPUP,
  BEST_OFFERS_INFO,
} from "../../constants";
import { defaultPreset } from "../../hooks/useChart/presets";
import { defaultPresetReport } from "../../components/Reports/presets";
import { defaultPaymentHistoryPreset } from "../../components/PaymentHistoryActions/presets";

export type LoadingKeys =
  | typeof USER_INFO
  | typeof MANAGER_INFO
  | typeof EVENTS_POSTBACK
  | typeof ADDITIONAL_POSTBACK
  | typeof MAIN_REPORT_TABLE
  | typeof TRACKING_CODE_LOADING
  | typeof OUTSTANDING_BALANCE
  | typeof INFO_MAIN_POPUP
  | typeof BEST_OFFERS_LIST;
export type State = {
  form: FormState;
  loading: LoadingState;
  notifications: NotificationsState;
  errors: ErrorsState;
  userInfo: UserInfoState;
  managerInfo: ManagerInfoState;
  tabs: TabsState;
  dictionaries: DictionariesState;
  eventsPostback: EventsPostbackState;
  trackingTools: TrackingToolsState;
  additionalPostback: AdditionalPostbackState;
  tables: TableState;
  outstandingBalanceInfo: OutstandingBalanceInfoState;
  linkGenerator: LinkGeneratorState;
};
export type Actions = AnyAction;
export type CabinetStore = Store<State, Actions>;
export const mapCabinetToState: () => State = () => ({
  linkGenerator: initLinkGeneratorState({
    selectedValues: {
      isTraffic: "0",
      isStraight: "1",
      isMainstream: "0",
      isPreLander: "1",
      isPPL: "1",
    },
    groups: [
      {
        title: "Traffic Type",
        value: "isTraffic",
      },
      {
        title: "Niche",
        value: "isStraight",
      },
      {
        title: "Safe parameter",
        value: "isMainstream",
      },
      {
        title: "Pre-Lander Settings",
        value: "isPreLander",
      },
      {
        title: "Payment model",
        value: "isPPL",
      },
    ],
    optionsList: optionsList as unknown as DeepMutable<typeof optionsList>,
  }),
  form: initFormState({
    [NO_PAYMENT_DETAILS_MODAL]: {
      open: false,
    },
    [NATURE_OF_TRAFFIC_MODAL]: {
      open: false,
    },
    [INFO_BANNER]: {
      open: false,
      openModal: false,
    },
    [PAYMENT_METHOD_FORM]: {
      isEdit: true,
      paymentMethodId: -1,
      accountNumber: "",
      bankAccountName: "",
      bankAddress: "",
      bankName: "",
      beAddress: "",
      beCountry: "",
      beName: "",
      bitcoin: "",
      capitalistWallet: "",
      genome: "",
      iban: "",
      paxumMail: "",
      paymentNote: "",
      payoneerWallet: "",
      payoutCountry: "",
      payPalWallet: "",
      paysera: "",
      qiwi: "",
      swiftCode: "",
      vatNumber: "",
      webmoneyWallet: "",
      yandex: "",
      usdt: "",
    },
    [USER_INFO_FORM]: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmation: "",
      imType: "",
      imName: "",
      howFoundOut: "",
      photo: "",
      howFoundOutComment: "",
      revenue: "",
      topCountries: "",
      topVerticals: "",
      topVerticalsComment: "",
      natureOfTraffic: "",
    },
    [CONTACT_INFO_FORM]: {
      companyName: "",
      companyUrl: "",
      companyType: "",
      address: "",
      country: "",
      taxSSN: "",
      phoneNumber: "",
    },
    [BEST_OFFERS_INFO]: {
      title: "",
    },
  }),
  loading: initLoadingState({
    [INFO_MAIN_POPUP]: true,
    [USER_INFO]: true,
    [MANAGER_INFO]: true,
    [EVENTS_POSTBACK]: true,
    [ADDITIONAL_POSTBACK]: false,
    [MAIN_REPORT_TABLE]: false,
    [TRACKING_CODE_LOADING]: false,
    [OUTSTANDING_BALANCE]: false,
    [BEST_OFFERS_LIST]: true,
  }),
  tabs: initTabsState({
    [PROFILE_PAGE]: {
      tabs: [USER_INFO_TAB, CONTACT_INFO_TAB, PAYMENT_METHODS_TAB],
      current: USER_INFO_TAB,
    },
    [REPORTS_PAGE]: {
      tabs: [MAIN_REPORT_TAB, TRANSACTION_REPORT_TAB],
      current: MAIN_REPORT_TAB,
    },
    [POSTBACKS_PAGE]: {
      tabs: [
        DEFAULT_POSTBACK_TAB,
        ADDITIONAL_POSTBACKS_TAB,
        INFO_POSTBACKS_TAB,
      ],
      current: DEFAULT_POSTBACK_TAB,
    },
  }),
  tables: initTableState({
    [DASHBOARD_TABLE]: {
      filters: {
        preset: defaultPreset.value,
        dateFrom: defaultPreset.start,
        dateTo: defaultPreset.end,
      },
    },
    [MAIN_REPORT_TABLE]: {
      sorting: {
        date: "desc",
      },
      filters: {
        date: {
          from: defaultPresetReport.start,
          to: defaultPresetReport.end,
        } as any, // todo need change type table
        groupBy: "date",
      },
      fields: [
        "date",
        "smartClicks",
        "smartClick2lead",
        "leads",
        "installs",
        "sales",
        "epc",
        "total_revenue",
      ],
      pendingFields: [
        "date",
        "smartClicks",
        "smartClick2lead",
        "leads",
        "installs",
        "sales",
        "epc",
        "total_revenue",
      ],
    },
    [PAYMENT_HISTORY_TABLE]: {
      filters: {
        dateFrom: defaultPaymentHistoryPreset.start,
        dateTo: defaultPaymentHistoryPreset.end,
      },
      fields: [
        "id",
        "created",
        "dateFrom",
        "dateTo",
        "paidTotal",
        "paidAt",
        "currency",
        "stateId",
        "isDownloadable",
        "isGroupInvoice",
      ],
    },
    [BEST_OFFERS_LIST]: {},
    [TRANSACTION_REPORT_TABLE]: {
      sorting: {
        date: "desc",
      },
      filters: {
        date: {
          from: defaultPresetReport.start,
          to: defaultPresetReport.end,
        } as any, // todo need change type table
      },
      fields: [
        "date",
        "conversion_time",
        "commission",
        "country",
        "subId",
        "platform",
        "status",
        "decline_reason",
        "clickId",
      ],
      pendingFields: [
        "date",
        "conversion_time",
        "commission",
        "country",
        "subId",
        "platform",
        "status",
        "decline_reason",
        "clickId",
      ],
    },
  }),
  notifications: initNotificationsState(),
  errors: initErrorsState(),
  userInfo: initUserInfoState(),
  managerInfo: initManagerInfoState(),
  dictionaries: initDictionariesState(),
  eventsPostback: initEventsPostbackState(),
  trackingTools: initTrackingToolsState(),
  additionalPostback: initAdditionalPostbackState(),
  outstandingBalanceInfo: initOutstandingBalanceInfoState(),
});

const reducers = combineReducers({
  linkGenerator,
  form: formReducer,
  loading: loadingReducer,
  notifications: notificationsReducer,
  errors: errorsReducer,
  userInfo,
  managerInfo,
  tabs,
  dictionaries,
  eventsPostback,
  trackingTools,
  additionalPostback,
  tables,
  outstandingBalanceInfo,
});

const sagaMiddleware = createSagaMiddleware();

export default (): CabinetStore => {
  const composeEnhancers =
    // eslint-disable-next-line no-underscore-dangle
    typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      serialize: {
        immutable: Immutable,
      },
    })
      : compose;
  const store = createStore(
    reducers,
    mapCabinetToState(),
    composeEnhancers(applyMiddleware(sagaMiddleware))
  );
  sagaMiddleware.run(mainSaga);
  return store;
};
